import { useQuery, useMutation } from "@apollo/client/react"
import { Button, CircularProgress } from "@mui/material"
import { Formik } from "formik"
import React, { useState, useMemo, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"

import AlertMessages from "@/components/settings/opening_hours/alert_messages"
import RegularHoursSection from "@/components/settings/opening_hours/regular_hours_section"
import SpecialHoursSection from "@/components/settings/opening_hours/special_hours_section"
import { getInitialValues, type FormValues } from "@/components/settings/opening_hours/utils"
import {
  GetOpeningHoursDocument,
  type GetOpeningHoursQuery,
  type GetOpeningHoursQueryVariables,
  UpdateVenueWorkingHoursDocument,
  type UpdateVenueWorkingHoursMutation,
  type UpdateVenueWorkingHoursMutationVariables,
  OpeningHourKind,
  type VenueWorkingHourInput,
} from "@/graphql/graphql"
import i18n from "@/i18n"
import { usePanelLink } from "@/shared/hooks"
import { showErrorToast } from "@/shared/toast"

const STYLES = {
  container: "flex flex-col gap-6 max-w-4xl",
  header: "text-3xl font-bold mb-0",
} as const

const OpeningHours: React.FC = () => {
  const { venueSlug } = useParams()
  const navigate = useNavigate()
  const linkTo = usePanelLink()
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const { data, loading, error, refetch } = useQuery<GetOpeningHoursQuery, GetOpeningHoursQueryVariables>(
    GetOpeningHoursDocument,
    {
      variables: { slug: venueSlug },
    },
  )

  const [updateMutation, { loading: saving }] = useMutation<
    UpdateVenueWorkingHoursMutation,
    UpdateVenueWorkingHoursMutationVariables
  >(UpdateVenueWorkingHoursDocument)

  const venue = data?.venue

  useEffect(() => {
    if (venue && !venue.permissions?.manageVenue) {
      navigate(linkTo("settings"))
    }
  }, [venue?.permissions?.manageVenue, navigate, linkTo])

  const initialValues: FormValues = useMemo(() => getInitialValues(venue?.openingHours), [venue])

  const handleSubmit = async (values: FormValues) => {
    if (!venue) return

    setSuccessMessage(null)
    setErrorMessage(null)

    try {
      const workingHours: VenueWorkingHourInput[] = [
        ...values.regularHours.map((hour) => ({
          id: hour.id,
          kind: OpeningHourKind.Regular,
          weekday: hour.weekday,
          closed: hour.closed,
          startTime: hour.closed ? null : hour.startTime,
          endTime: hour.closed ? null : hour.endTime,
          startDate: null,
          endDate: null,
        })),
        ...values.specialHours.map((hour) => ({
          id: hour.id,
          kind: OpeningHourKind.Special,
          closed: hour.closed,
          startDate: hour.startDate,
          endDate: hour.endDate,
          startTime: hour.closed ? null : hour.startTime,
          endTime: hour.closed ? null : hour.endTime,
          weekday: null,
        })),
      ]

      const result = await updateMutation({
        variables: {
          venueId: venue.id,
          workingHours,
        },
      })

      if (result.data?.updateVenueWorkingHours?.success) {
        void refetch()
        setSuccessMessage(i18n.t("app.settings.venues.opening_hours.success"))
      } else {
        setErrorMessage(i18n.t("app.settings.venues.opening_hours.error"))
      }
    } catch (err) {
      showErrorToast()
      console.error("Error updating opening hours:", err)
      setErrorMessage(i18n.t("app.settings.venues.opening_hours.error"))
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <CircularProgress />
      </div>
    )
  }

  if (error) {
    return <div className="text-red-500 p-8">Error loading venue: {error.message}</div>
  }

  if (!venue) {
    return <div className="p-8">Venue not found</div>
  }

  return (
    <Formik initialValues={initialValues} enableReinitialize={true} onSubmit={handleSubmit}>
      {(formik) => (
        <form onSubmit={formik.handleSubmit} className={STYLES.container} data-testid="settings-venue-hours-form">
          <h1 className={STYLES.header} data-testid="settings-venue-hours-title">
            {i18n.t("app.settings.venues.opening_hours.title")}
          </h1>

          <AlertMessages
            successMessage={successMessage}
            errorMessage={errorMessage}
            onCloseSuccess={() => setSuccessMessage(null)}
            onCloseError={() => setErrorMessage(null)}
          />

          <RegularHoursSection regularHours={formik.values.regularHours} formik={formik} />

          <SpecialHoursSection specialHours={formik.values.specialHours} formik={formik} />

          <div className="flex justify-end gap-3">
            <Button
              variant="outlined"
              color="primary"
              onClick={() => formik.resetForm()}
              disabled={saving || !formik.dirty}
              data-testid="settings-venue-hours-btn-cancel"
            >
              {i18n.t("app.settings.venues.opening_hours.cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={saving || !formik.dirty}
              data-testid="settings-venue-hours-btn-save"
            >
              {saving ? <CircularProgress size={24} /> : i18n.t("app.settings.venues.opening_hours.save_changes")}
            </Button>
          </div>
        </form>
      )}
    </Formik>
  )
}

export default OpeningHours
